import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import './firstaid-adult.css';
import Button from '../../components/Inputs/Button/Button';
import kitoBuddy from '../../assets/kitoBuddy/Kito Buddy for Wave Animation_1 1.png';
import SpeechCard from './components/SpeechCard/SpeechCard';
import poisoning from '../../assets/designSystem/icons/firstAid/poisoning/Swallowed Poison.svg';
import choking from '../../assets/designSystem/icons/StaticPages/FirstAid/Choking Illustration.svg';
import nosebleed from '../../assets/designSystem/icons/StaticPages/FirstAid/NosebleedArtboard 1.svg';
import fractures from '../../assets/designSystem/icons/StaticPages/FirstAid/FracturesArtboard 1.svg';
import cuts from '../../assets/designSystem/icons/StaticPages/FirstAid/Cuts and WoundsArtboard 1.svg';
import burns from '../../assets/designSystem/icons/StaticPages/FirstAid/Burns and ScaldsArtboard 1.svg';
import doctormascot from '../../assets/designSystem/icons/StaticPages/FirstAid/doctormascot.svg';
import warningIcon from '../../assets/designSystem/icons/StaticPages/FirstAid/warning.svg';
import hospital from '../../assets/designSystem/icons/StaticPages/FirstAid/hospital.svg';
import AskMascot from '../../assets/kitoBuddy/Kito-Asking-What-Is 1.png';
import WhatToDoMascot from '../../assets/kitoBuddy/Kito-Asking-What-to-do 3.png';
import WhenToWorryMascot from '../../assets/kitoBuddy/Kito-Asking-When-To-Worry 1.png';
import CelebMascot from '../../assets/kitoBuddy/Celebration.png';
import AidCard from './components/AidCard/AidCard';
import ChatBubble from '../../components/Chat/Chat';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import { getInitialStepForPath } from './utils';
import BaseLayout from '../../commons/BaseLayout';
import buddyForWave from '../../assets/kitoBuddy/homepage.png';

const VERSION_STORAGE_KEY = '@kitoApp/version';
export default function FirstAidAdult() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [activePath, setActivePath] = useState({});
  const [showPaths, setShowPaths] = useState(true);
  const [activePathName, setActivePathName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [activeVersion, setActiveVersion] = useState(() => window.localStorage.getItem(VERSION_STORAGE_KEY) || 'Adult');
  const [videoLink, setVideoLink] = useState('');

  const warningLabel = t('firstAid.warning');
  const warningAlt = t('firstAid.warningAlt');
  const hospitalAlt = t('firstAid.hospitalAlt');

  useEffect(() => {
    const stored = localStorage.getItem('firstAidProgressData');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }

    setCurrentStep(getInitialStepForPath(savedProgress, activePathName));
  }, [activePathName]);

  useEffect(() => {
    const stored = localStorage.getItem('firstAidProgressData');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }
    if (currentStep === 1 || currentStep === 0 || !activePathName) {
      return;
    }
    const normalizedStep = Math.max(1, currentStep);

    const progressVal = currentStep > totalSteps ? 100 : ((currentStep - 1) / totalSteps) * 100;
    const updatedProgress = {
      ...savedProgress,
      [activePathName]: {
        step: normalizedStep,
        progress: progressVal,
      },
    };

    localStorage.setItem('firstAidProgressData', JSON.stringify(updatedProgress));
  }, [currentStep, activePathName, totalSteps]);

  const chokingPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.choking.whatIsChoking')}`,
      videoLink: t('firstaid.child.choking.videoLink'),
      cards: [
        {
          body: `<p>${t('firstAid.choking.whatIsChokingDescription')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: AskMascot,
      dialogue: `${t('firstAid.choking.howToTell')}`,
      cards: [
        {
          body: `<ol><li>${t('firstAid.choking.suddenlyCantSpeak')}</li><li>${t('firstAid.choking.weakCough')}</li><li>${t('firstAid.choking.looksPanicked')}</li></ol>`,
          backgroundColor: '#fff',
          textColor: '#000',
          outline: '1px solid #FF8A00',
        },
      ],
    },
    4: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.choking.whatToDo')}`,
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.choking.whatToDo')}</h3><p>${t('firstAid.choking.stayCalm')}</p><ul className="checkbox__list"><li><input type="checkbox" value="under1"/> ${t('firstAid.choking.babyUnder1')}</li><li><input type="checkbox" value="over1" /> ${t('firstAid.choking.babyOver1')}</li></ul></p>`,
          backgroundColor: '#14C7C4',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.choking.whatToDo')}`,
      under1: {
        body: `<h3 className="card__title">${t('firstAid.choking.babyUnder1')}</h3><ol><li>${t('firstAid.choking.callForHelp')}</li><li>${t('firstAid.choking.layBabyFaceDown')}</li><li>${t('firstAid.choking.give5FirmBackBlows')}</li><li>${t('firstAid.choking.checkAfterEachBlowAndPush')}</li><li>${t('firstAid.choking.stillChokingTurnFaceUp')}</li><li>${t('firstAid.choking.repeat5Blows5Pushes')}</li></ol>`,
      },
      over1: {
        body: `<h3 className="card__title">${t('firstAid.choking.babyOver1')}</h3><ol><li> ${t('firstAid.choking.tellToCoughOrKeepCoughing')}</li><li> ${t('firstAid.choking.leanThemForwardGive5FirmBackBlows')}</li><li> ${t('firstAid.choking.stillChokingStandBehind')}</li><li> ${t('firstAid.choking.repeat5Blows5Pulls')}</li></ol>`,
      },
    },
    6: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.choking.whatNotToDo')}`,
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.choking.whatNotToDo')}</h3><ol><li>${t('firstAid.choking.doNotShakeYourChild')}</li><li>${t('firstAid.choking.doNotHoldYourChildByTheLegs')}</li><li>${t('firstAid.choking.doNotPutFingersInYourChildsMouth')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    7: {
      image: kitoBuddy,
      dialogue: `${t('firstAid.choking.whenToSeekHelp')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3 className="card__title"><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.choking.whenToSeekHelp')}</h3><div className="choking"><h4><img src="${hospital}" alt="${hospitalAlt}" />${t('firstAid.choking.gotohospital')}</h4><ul><li>${t('firstAid.choking.stillChoking')}</li><li>${t('firstAid.choking.noResponse')}</li><li>${t('firstAid.choking.noisyBreathing')}</li></ul></div><h3 className="warning-span"><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.choking.alwaysSeekHelp')}</h3>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
          title: `<h3>${t('firstAid.choking.whenToSeekHelp')}</h3>`,
        },
      ],
    },
  };

  const noseBleedPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.nosebleed.whatIsNoseBleed')}`,
      cards: [
        {
          body: `<p>${t('firstAid.nosebleed.noseBleedDescription')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.nosebleed.whatToDo')}`,
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.nosebleed.whatToDo')}</h3><ol><li>${t('firstAid.nosebleed.whatToDoL1')}</li><li>${t('firstAid.nosebleed.whatToDoL2')}</li><li>${t('firstAid.nosebleed.whatToDoL3')}</li><li>${t('firstAid.nosebleed.whatToDoL4')}</li><li>${t('firstAid.nosebleed.whatToDoL5')}</li><li>${t('firstAid.nosebleed.whatToDoL6')}</li><li>${t('firstAid.nosebleed.whatToDoL7')}</li><li>${t('firstAid.nosebleed.whatToDoL8')}</li><li>${t('firstAid.nosebleed.whatToDoL9')}</li></ol>`,
          backgroundColor: '#14C7C4',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    4: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.nosebleed.whatNotToDo')}`,
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.nosebleed.whatNotToDo')}</h3><ol><li>${t('firstAid.nosebleed.whatNotToDoL1')}</li><li>${t('firstAid.nosebleed.whatNotToDoL2')}</li><li>${t('firstAid.nosebleed.whatNotToDoL3')}</li><li>${t('firstAid.nosebleed.whatNotToDoL4')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.nosebleed.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.nosebleed.gotohospital')}</h3><ul className="worry_list"><li>${t('firstAid.nosebleed.whenToWorryL1')}</li><li>${t('firstAid.nosebleed.whenToWorryL2')}</li><li>${t('firstAid.nosebleed.whenToWorryL3')}</li><li>${t('firstAid.nosebleed.whenToWorryL4')}</li><li>${t('firstAid.nosebleed.whenToWorryL5')}</li><li>${t('firstAid.nosebleed.whenToWorryL6')}</li><li>${t('firstAid.nosebleed.whenToWorryL7')}</li><li>${t('firstAid.nosebleed.whenToWorryL8')}</li><li>${t('firstAid.nosebleed.whenToWorryL9')}</li></ul>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
  };

  const fracturesPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.fractures.whatIsAFracture')}`,
      cards: [
        {
          body: `<p>${t('firstAid.fractures.factureDescription')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3></span><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.fractures.whatTodo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.fractures.whatTodo')}</h3><ol><li>${t('firstAid.fractures.L1')}</li><li>${t('firstAid.fractures.L2')}</li><li>${t('firstAid.fractures.L3')}</li><li>${t('firstAid.fractures.L4')}</li></ol>`,
          backgroundColor: '#14C7C4',
          textColor: '#FFF',
          outline: 'none',
        },
      ],
    },
    4: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.fractures.whatNotToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.fractures.whatNotToDo')}</h3><ol><li>${t('firstAid.fractures.whatNotToDoL1')}</li><li>${t('firstAid.fractures.whatNotToDoL2')}</li><li>${t('firstAid.fractures.whatNotToDoL3')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.fractures.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3>${t('firstAid.fractures.gotohospital')}</h3><ul className="worry_list"><li>${t('firstAid.fractures.whenToWorryL1')}</li><li>${t('firstAid.fractures.whenToWorryL2')}</li><li>${t('firstAid.fractures.whenToWorryL3')}</li><li>${t('firstAid.fractures.whenToWorryL4')}</li></ul>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
  };

  const poisoningPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.poisoning.whatIsPoisoning')}`,
      cards: [
        {
          body: `<p>${t('firstAid.poisoning.poisoningDescription')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3></span><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.poisoning.whatToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.poisoning.whatToDo')}</h3><ol><li>${t('firstAid.poisoning.whatToDoL1')}</li><li>${t('firstAid.poisoning.whatToDoL2')}</li><li>${t('firstAid.poisoning.whatToDoL3')}</li></ol>`,
          backgroundColor: '#14C7C4',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    4: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.poisoning.whatNotToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.poisoning.whatNotToDo')}</h3><ol><li>${t('firstAid.poisoning.whatNotToDoL1')}</li><li>${t('firstAid.poisoning.whatNotToDoL2')}</li><li>${t('firstAid.poisoning.whatNotToDoL3')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.poisoning.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3>${t('firstAid.poisoning.gotohospital')}</h3><ul className="worry_list"><li>${t('firstAid.poisoning.whenToWorryL1')}</li><li>${t('firstAid.poisoning.whenToWorryL2')}</li><li>${t('firstAid.poisoning.whenToWorryL3')}</li><li>${t('firstAid.poisoning.whenToWorryL4')}</li></ul>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    6: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.poisoning.prevention')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.poisoning.prevention')}</h3><ul className="worry_list"><li>${t('firstAid.poisoning.preventionL1')}</li><li>${t('firstAid.poisoning.preventionL2')}</li></ul><h3 className="warning-span"><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.choking.alwaysSeekHelp')}</h3>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
  };

  const burnsandscaldsPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.burnsandscalds.whatIsABurn')}`,
      cards: [
        {
          body: `<p>${t('firstAid.burnsandscalds.aBurnIsAnInjuryToTheSkin')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3></span><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.burnsandscalds.whatToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.burnsandscalds.whatToDo')}</h3><ol><li>${t('firstAid.burnsandscalds.whatToDoL1')}</li><li>${t('firstAid.burnsandscalds.whatToDoL2')}</li><li>${t('firstAid.burnsandscalds.whatToDoL3')}</li><li>${t('firstAid.burnsandscalds.whatToDoL4')}</li><li>${t('firstAid.burnsandscalds.whatToDoL5')}</li></ol>`,
          backgroundColor: '#14C7C4',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    4: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.burnsandscalds.whatNotToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.burnsandscalds.whatNotToDo')}</h3><ol><li>${t('firstAid.burnsandscalds.whatNotToDoL1')}</li><li>${t('firstAid.burnsandscalds.whatNotToDoL2')}</li><li>${t('firstAid.burnsandscalds.whatNotToDoL3')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.burnsandscalds.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.burnsandscalds.gotohospital')}</h3><ol><li>${t('firstAid.burnsandscalds.whenToWorryL1')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL2')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL3')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL4')}</li></ol>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
  };

  const cutsandwoundsPath = {
    2: {
      image: AskMascot,
      dialogue: `${t('firstAid.cutsandwounds.whatIsACutOrWounds')}`,
      cards: [
        {
          body: `<p>${t('firstAid.cutsandwounds.cutsAndWoundsDescription')}</p>`,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          outline: '1px solid #FF8A00',
        },
        {
          body: `<h3 className="card__title whatIs"><img src="${warningIcon}" alt="${warningAlt}" />${warningLabel}</h3></span><p>${t('firstAid.choking.chokingEmergency')}</p>`,
          backgroundColor: '#FF2B26',
          textColor: '#000',
          type: 'warning',
          outline: 'none',
        },
      ],
    },
    3: {
      image: WhatToDoMascot,
      dialogue: `${t('firstAid.cutsandwounds.whatToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.cutsandwounds.whatToDo')}</h3><ol><li>${t('firstAid.cutsandwounds.whatToDoL1')}</li><li>${t('firstAid.cutsandwounds.whatToDoL2')}</li><li>${t('firstAid.cutsandwounds.whatToDoL3')}</li><li>${t('firstAid.cutsandwounds.whatToDoL4')}</li></ol>`,
          backgroundColor: '#14C7C4',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    4: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.cutsandwounds.whatNotToDo')}`,
      cards: [
        {
          body: `<h3>${t('firstAid.cutsandwounds.whatNotToDo')}</h3><ol><li>${t('firstAid.cutsandwounds.whatNotToDoL1')}</li></ol>`,
          backgroundColor: '#FF8A00',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
    5: {
      image: WhenToWorryMascot,
      dialogue: `${t('firstAid.cutsandwounds.whentoWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          body: `<h3 className="card__title">${t('firstAid.cutsandwounds.whentoWorry')}</h3><ol><li>${t('firstAid.cutsandwounds.whentoWorryL1')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL2')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL3')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL4')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL5')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL6')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL7')}</li></ol>`,
          backgroundColor: '#FF2B26',
          textColor: '#fff',
          outline: 'none',
        },
      ],
    },
  };

  const aidPaths = [
    {
      name: 'choking',
      path: chokingPath,
      title: t('firstAid.paths.choking'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.choking') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).choking?.progress || 0
        : 0,
      image: choking,
      imgBgColor: '#2C75BA',
      backgroundColor: 'red',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(chokingPath);
        setActivePathName('choking');
        setTotalSteps(Object.keys(chokingPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.choking.videoLink'));
      },
    },
    {
      name: 'fractures',
      path: fracturesPath,
      title: t('firstAid.paths.fractures'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.fractures') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).fractures?.progress || 0
        : 0,
      image: fractures,
      imgBgColor: '#FF8A00',
      backgroundColor: 'orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(fracturesPath);
        setActivePathName('fractures');
        setTotalSteps(Object.keys(fracturesPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.fractures.videoLink'));
      },
    },
    {
      name: 'nosebleed',
      path: noseBleedPath,
      title: t('firstAid.paths.nosebleed'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.nosebleed') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).nosebleed?.progress || 0
        : 0,
      image: nosebleed,
      imgBgColor: '#2C75BA',
      backgroundColor: 'yellow',
      progressColor: 'yellow',
      onclick: () => {
        setShowPaths(false);
        setActivePath(noseBleedPath);
        setActivePathName('nosebleed');
        setTotalSteps(Object.keys(noseBleedPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.nosebleeding.videoLink'));
      },
    },
    {
      name: 'poisoning',
      path: poisoningPath,
      title: t('firstAid.paths.poisoning'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.poisoning') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).poisoning?.progress || 0
        : 0,
      image: poisoning,
      imgBgColor: '#FF8A00',
      backgroundColor: 'orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(poisoningPath);
        setActivePathName('poisoning');
        setTotalSteps(Object.keys(poisoningPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.poisoning.videoLink'));
      },
    },
    {
      name: 'burnsandscalds',
      path: burnsandscaldsPath,
      title: t('firstAid.paths.burnsandscalds'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.burnsandscalds') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).burnsandscalds?.progress || 0
        : 0,
      image: burns,
      imgBgColor: '#2C75BA',
      backgroundColor: 'blue',
      progressColor: 'yellow',
      onclick: () => {
        setShowPaths(false);
        setActivePath(burnsandscaldsPath);
        setActivePathName('burnsandscalds');
        setTotalSteps(Object.keys(burnsandscaldsPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.burns.videoLink'));
      },
    },
    {
      name: 'cutsandwounds',
      path: cutsandwoundsPath,
      title: t('firstAid.paths.cutsandwounds'),
      subTitle: t('firstAid.pathCardSubtitle', { aid: t('firstAid.paths.cutsandwounds') }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).cutsandwounds?.progress || 0
        : 0,
      image: cuts,
      imgBgColor: '#FF8A00',
      backgroundColor: '#orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(cutsandwoundsPath);
        setActivePathName('cutsandwounds');
        setTotalSteps(Object.keys(cutsandwoundsPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.cuts.videoLink'));
      },
    },
  ];

  function handleSearch(e) {
    setSearchQuery(e.target.value);
  }

  function resetProgress() {
    setCurrentStep(1);
    setSelectedAgeGroup(null);
    // if (window.confirm('Are you sure you want to reset your progress?')) {
    //   setCurrentStep(1);
    //   setSelectedAgeGroup(null);
    // }
  }

  const completionPathName = aidPaths.find((pathway) => pathway.name === activePathName)?.name || 'default';
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredPaths = aidPaths.filter(
    (path) =>
      path.title.toLowerCase().includes(normalizedSearchQuery) ||
      path.subTitle.toLowerCase().includes(normalizedSearchQuery)
  );

  const getVideoId = (url) => {
    if (!url) return null;
    const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/;
    const match = url.match(regExp);
    return match ? match[1] : url;
  };

  const videoId = getVideoId(t(videoLink));
  return activeVersion === 'Child' ? (
    <Navigate to="/homepage" replace />
  ) : (
    <BaseLayout header headerLogo sidebar onVersionChange={setActiveVersion} fitHeight>
      {showPaths ? (
        <div className="firstAid__browse">
          <div className="firstAid_header">
            <div className="firstAid__intro">
              <h2>{t('firstAid.title')}</h2>
              <img src={doctormascot} alt={t('firstAid.animationAlt')} />
            </div>
            <input
              name="search_first_aid"
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('firstAid.searchPlaceholder')}
              className="firstAid__search"
            />
          </div>
          <div className="firstAid__aidCards">
            {filteredPaths.map((aid) => (
              <AidCard
                key={aid.title}
                title={aid.title}
                description={aid.subTitle}
                progressLabel={t('firstAid.progressLabel')}
                percent={Math.round(aid.progress)}
                imageSrc={aid.image}
                backgroundColor={aid.backgroundColor}
                progressColor={aid.progressColor}
                imgBgColor={aid.imgBgColor}
                onClick={aid.onclick}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="firstaidPage__content">
          <div className="progress-section">
            {(() => {
              let progressValue = 0;
              if (currentStep === 1) {
                progressValue = 0;
              } else if (currentStep > totalSteps) {
                progressValue = 100;
              } else {
                progressValue = ((currentStep - 1) / totalSteps) * 100;
              }
              return (
                <ProgressBar
                  width={100}
                  percent={progressValue}
                  controls
                  onClose={() => setShowPaths(true)}
                  resetProgress={() => resetProgress()}
                  resetProgressIcon
                />
              );
            })()}
          </div>
          {currentStep === 1 && (
            <div className="firstAid__start">
              <div>
                <ChatBubble text={t('staticPages.homePageAdult.greeting')} img={buddyForWave} />
                <div className="video-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={t('firstAid.videoPlayerTitle')}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
              <Button
                hierarchy="primary"
                width="defaultWidth"
                onClick={() => {
                  setCurrentStep(2);
                }}
              >
                {t('firstAid.nextButton')}
              </Button>
            </div>
          )}

          {currentStep > 1 && currentStep < totalSteps + 1 && (
            <div className="journey">
              <div className="firstAid__progress">
                <ChatBubble 
                  text={activePath[currentStep].dialogue}  
                  img={activePath[currentStep].image} 
                  outline={activePath[currentStep].outline}
                />
                {activePathName === 'choking' && currentStep === 4 && (
                  <div className="card" style={{ backgroundColor: '#14C7C4', color: '#fff', border: 'none' }}>
                    <h3 className="card__title">{t('firstAid.choking.whatToDo')}</h3>
                    <p>{t('firstAid.choking.stayCalm')}</p>
                    <ul className="checkbox__list">
                      <li>
                        <input
                          type="checkbox"
                          value="under1"
                          checked={selectedAgeGroup === 'under1'}
                          onChange={(e) => {
                            setSelectedAgeGroup(e.target.value);
                          }}
                        />
                        <span>{t('firstAid.choking.babyUnder1')}</span>
                      </li>
                      <li>
                        <input
                          type="checkbox"
                          value="over1"
                          checked={selectedAgeGroup === 'over1'}
                          onChange={(e) => {
                            setSelectedAgeGroup(e.target.value);
                          }}
                        />
                        <span>{t('firstAid.choking.babyOver1')}</span>
                      </li>
                    </ul>
                  </div>
                )}

                {activePathName === 'choking' && currentStep === 5 && selectedAgeGroup && (
                  <SpeechCard
                    backgroundColor="#14C7C4"
                    textColor="#fff"
                    outline="none"
                    body={activePath[currentStep][selectedAgeGroup]?.body}
                  />
                )}
                {activePath[currentStep].cards &&
                  !(activePathName === 'choking' && (currentStep === 4 || currentStep === 5)) &&
                  activePath[currentStep].cards.map((card) => (
                    <SpeechCard
                      key={card.name}
                      backgroundColor={card.backgroundColor}
                      textColor={card.textColor}
                      outline={card.outline}
                      title={card.title}
                      body={card.body}
                    />
                  ))}
              </div>
              <div className="firstAid__footer">
                <Button
                  hierarchy="secondary"
                  width="defaultWidth"
                  onClick={() => {
                    const newStep = Math.max(currentStep - 1, 1);
                    setCurrentStep(newStep);
                  }}
                >
                  {t('firstAid.backButton')}
                </Button>
                <Button
                  hierarchy="primary"
                  width="defaultWidth"
                  disabled={activePathName === 'choking' && currentStep === 4 && !selectedAgeGroup}
                  onClick={() => {
                    if (activePathName === 'choking' && currentStep === 4 && !selectedAgeGroup) {
                      setSelectedAgeGroup('under1');
                    }
                    const newStep = Math.min(currentStep + 1, totalSteps + 1);
                    setCurrentStep(newStep);
                  }}
                >
                  {t('firstAid.nextButton')}
                </Button>
              </div>
            </div>
          )}

          {currentStep === totalSteps + 1 && (
            <div className="firstAid__completion">
              <ChatBubble 
                text={t(`firstAid.${completionPathName}.congrats`)}
                img={CelebMascot} 
              />
              <Button
                hierarchy="primary"
                width="defaultWidth"
                onClick={() => {
                  setShowPaths(true);
                  setActivePath({});
                  setCurrentStep(0);
                  setSelectedAgeGroup(null);
                  navigate('/firstaid-adult');
                }}
              >
                {t('firstAid.goToPageButton')}
              </Button>
            </div>
          )}
        </div>
      )}
    </BaseLayout>
  );
}
